import React, { useState, useEffect, useCallback } from 'react';
import './ConversationManager.css';

function ConversationManager({ isOpen, onClose, token, api }) {
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [toast, setToast] = useState(null);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }), [token]);

  const flash = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Load conversations
  useEffect(() => {
    if (!isOpen || !token) return;
    setLoading(true);
    fetch(`${api}/user_conversations`, { headers: headers() })
      .then(r => r.json())
      .then(data => {
        setMessages(data.messages || []);
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, [isOpen, token, api, headers]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const res = await fetch(`${api}/conversations/search?q=${encodeURIComponent(searchQuery)}`, { headers: headers() });
      const data = await res.json();
      setSearchResults(data.results || []);
    } catch { flash('Search failed', 'error'); }
    setSearching(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${api}/conversations/export`, { headers: headers() });
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `heartfelt-conversations-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      flash(`Exported ${data.total} messages`);
    } catch { flash('Export failed', 'error'); }
    setExporting(false);
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      const res = await fetch(`${api}/conversations/clear`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (data.success) {
        setMessages([]);
        setSearchResults(null);
        setShowClearConfirm(false);
        flash(`Cleared ${data.deleted} messages`);
      } else flash('Clear failed', 'error');
    } catch { flash('Clear failed', 'error'); }
    setClearing(false);
  };

  if (!isOpen) return null;

  const displayMessages = searchResults !== null ? searchResults : messages;

  // Group by date
  const grouped = {};
  displayMessages.forEach(m => {
    const d = new Date(m.timestamp * 1000);
    const key = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(m);
  });
  const groups = Object.entries(grouped);

  return (
    <div className="cm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cm-panel">
        {toast && <div className={`cm-toast ${toast.type}`}>{toast.text}</div>}

        {/* Header */}
        <div className="cm-header">
          <h2 className="cm-title">Conversations</h2>
          <div className="cm-header-actions">
            <button className="cm-action-btn" onClick={handleExport} disabled={exporting || messages.length === 0}>
              {exporting ? 'Exporting…' : 'Export'}
            </button>
            {!showClearConfirm ? (
              <button
                className="cm-action-btn cm-action-danger"
                onClick={() => setShowClearConfirm(true)}
                disabled={messages.length === 0}
              >
                Clear All
              </button>
            ) : (
              <div className="cm-clear-confirm">
                <span className="cm-clear-warn">Delete all messages?</span>
                <button className="cm-action-btn cm-action-danger" onClick={handleClear} disabled={clearing}>
                  {clearing ? 'Clearing…' : 'Yes, delete'}
                </button>
                <button className="cm-action-btn" onClick={() => setShowClearConfirm(false)}>Cancel</button>
              </div>
            )}
            <button className="cm-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        {/* Search */}
        <div className="cm-search">
          <input
            className="cm-search-input"
            placeholder="Search conversations…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          />
          <button className="cm-search-btn" onClick={handleSearch} disabled={searching}>
            {searching ? '…' : 'Search'}
          </button>
          {searchResults !== null && (
            <button className="cm-search-clear" onClick={() => { setSearchResults(null); setSearchQuery(''); }}>
              Clear search ({searchResults.length} results)
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="cm-stats">
          {messages.length} total messages
          {searchResults !== null && ` · ${searchResults.length} matching`}
        </div>

        {/* Messages */}
        <div className="cm-messages">
          {loading ? (
            <div className="cm-loading">Loading conversations…</div>
          ) : groups.length === 0 ? (
            <div className="cm-empty">
              {searchResults !== null ? 'No messages match your search.' : 'No conversations yet.'}
            </div>
          ) : (
            groups.map(([date, msgs]) => (
              <div key={date} className="cm-date-group">
                <div className="cm-date-label">{date}</div>
                {msgs.map((msg) => (
                  <div key={msg.id || msg.timestamp} className={`cm-msg cm-msg-${msg.role}`}>
                    <div className="cm-msg-role">{msg.role === 'user' ? 'You' : 'Soul'}</div>
                    <div className="cm-msg-content">{msg.content}</div>
                    <div className="cm-msg-time">
                      {new Date(msg.timestamp * 1000).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default ConversationManager;
